/**
 * Ingredient Search Component
 *
 * Search bar to find and add raw products as ingredients.
 */

import { useState, useRef, useEffect } from 'react';
import { Search, Package, Plus } from 'lucide-react';
import { useDebounce } from '@/hooks';
import { useRawProducts } from '@/features/raw-products/hooks/useRawProducts';
import type { RawProductWithRelations } from '@bstock/shared';

interface IngredientSearchProps {
  onSelect: (product: RawProductWithRelations) => void;
  excludeIds?: string[];
}

export function IngredientSearch({ onSelect, excludeIds = [] }: IngredientSearchProps) {
  const [searchValue, setSearchValue] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const debouncedSearch = useDebounce(searchValue, 300);

  // Fetch raw products matching search
  const { data, isLoading } = useRawProducts({
    search: debouncedSearch || undefined,
    limit: 10,
    isActive: true,
    sortBy: 'name',
    sortOrder: 'asc',
  });

  // Filter out already added products
  const products = data?.data.filter((p) => !excludeIds.includes(p.id)) ?? [];

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (product: RawProductWithRelations) => {
    onSelect(product);
    setSearchValue('');
    setIsOpen(false);
    inputRef.current?.focus();
  };

  return (
    <div className="relative">
      {/* Search Input */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
        <input
          ref={inputRef}
          type="text"
          value={searchValue}
          onChange={(e) => {
            setSearchValue(e.target.value);
            setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
          placeholder="Rechercher un produit brut..."
          className="w-full pl-10 pr-4 py-2.5 bg-white border border-neutral-200 rounded-lg text-sm
                     placeholder:text-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-500
                     focus:border-transparent transition-all"
        />
      </div>

      {/* Dropdown Results */}
      {isOpen && searchValue.length > 0 && (
        <div
          ref={dropdownRef}
          className="absolute top-full left-0 right-0 mt-1 bg-white border border-neutral-200
                     rounded-lg shadow-lg max-h-64 overflow-y-auto z-20"
        >
          {isLoading ? (
            <div className="px-4 py-3 text-sm text-neutral-500 text-center">
              Recherche en cours...
            </div>
          ) : products.length > 0 ? (
            <ul>
              {products.map((product) => (
                <li key={product.id}>
                  <button
                    type="button"
                    onClick={() => handleSelect(product)}
                    className="w-full px-4 py-3 text-left hover:bg-neutral-50 transition-colors
                               flex items-center gap-3 group"
                  >
                    <div
                      className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{
                        backgroundColor: product.category.color
                          ? `${product.category.color}20`
                          : 'rgb(var(--color-neutral-100))',
                      }}
                    >
                      <Package
                        className="w-4 h-4"
                        style={{
                          color: product.category.color || 'rgb(var(--color-neutral-500))',
                        }}
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-dark-500 truncate">
                        {product.name}
                      </div>
                      <div className="text-xs text-neutral-400 truncate">
                        {product.sku} - {product.recipeUnit.symbol}
                      </div>
                    </div>
                    <Plus className="w-4 h-4 text-neutral-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <div className="px-4 py-3 text-sm text-neutral-500 text-center">
              Aucun produit trouve
            </div>
          )}
        </div>
      )}
    </div>
  );
}
